/**
 * search.js — Urdile
 * Punto 8 del documento "puntos para lanzar web".
 *
 * Qué resuelve:
 *
 *  · Ranking. Shopify ordena mal las consultas de modelo: "RCF SUB 705"
 *    devolvía 17 resultados con el 708-AS, el 808-AS y el 9007-AS antes que
 *    el 705-AS buscado. El comodín de Shopify ("RCF SUB 705*") sí acierta,
 *    pero aplicado siempre destroza el recall de las consultas genéricas
 *    (microfono pasa de 656 a 427 resultados, cable xlr de 275 a 106).
 *    Solución: se consultan las dos variantes en paralelo y las coincidencias
 *    del comodín se muestran primero, sin perder las demás.
 *
 *  · Guiones. "705-AS", "705 AS" y "705AS" tienen que encontrar lo mismo.
 *
 *  · Errores ortográficos. Shopify no corrige, pero su API de sugerencias
 *    (resources[type]=query) sí devuelve la forma correcta: "beyerdinamic"
 *    sugiere "beyerdynamic", "microfno" sugiere "microfono".
 *
 *  · Marcas. Se derivan del vendor de los productos encontrados y llevan al
 *    listado de esa marca.
 *
 *  · Sin resultados. En vez de un mensaje muerto, ofrece la corrección, las
 *    categorías y el contacto directo.
 */

'use strict';

const WA_URDILE = '56989021478';

/** Normaliza para comparar: sin acentos, sin guiones, minúsculas. */
function normalizar(texto) {
  return (texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** "RCF SUB 705" → "RCF SUB 705*" (comodín solo en el último término). */
function conComodin(consulta) {
  const t = consulta.trim();
  if (!t || t.endsWith('*')) return t;
  return t + '*';
}

/** ¿Parece un modelo o número de parte? Ahí el comodín es el que acierta. */
function pareceModelo(consulta) {
  return /\d/.test(consulta) || /[-/]/.test(consulta);
}

/** Levenshtein: cuántas ediciones separan dos textos. */
function distanciaEdicion(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let fila = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let anterior = fila[0];
    fila[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const temp = fila[j];
      fila[j] = Math.min(
        fila[j] + 1,                                    // borrar
        fila[j - 1] + 1,                                // insertar
        anterior + (a[i - 1] === b[j - 1] ? 0 : 1)      // sustituir
      );
      anterior = temp;
    }
  }
  return fila[b.length];
}

function debounceLocal(fn, espera) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), espera);
  };
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('input[type="search"], .site-header__search-input');
    this._dropdown = this.querySelector('.predictive-search__dropdown');
    this._form = this.querySelector('form') || this.closest('form');
    this._isOpen = false;
    this._currentIndex = -1;

    if (!this._input) return;

    this._buscar = debounceLocal(this.getSearchResults.bind(this), 250);
    this._bindEvents();
  }

  _bindEvents() {
    this._input.addEventListener('input', this.onChange.bind(this));
    this._input.addEventListener('focus', () => {
      if (this.getQuery().length >= 2) this.open();
    });
    this._input.addEventListener('keydown', this._onKeydown.bind(this));

    document.addEventListener('click', (evt) => {
      if (!this.contains(evt.target)) this.close();
    });

    // Enter sin nada seleccionado: se envía el formulario. Si la consulta
    // parece un modelo, se manda con comodín para que el resultado exacto
    // quede primero en la página de resultados.
    if (this._form) {
      this._form.addEventListener('submit', (evt) => {
        const q = this.getQuery();
        if (!q) {
          evt.preventDefault();
          return;
        }
        if (pareceModelo(q) && !q.endsWith('*')) {
          this._input.value = conComodin(q);
        }
      });
    }
  }

  onChange() {
    const query = this.getQuery();
    if (query.length < 2) {
      this.close();
      return;
    }
    this._buscar(query);
  }

  async _pedir(consulta, tipos, limite) {
    const params = new URLSearchParams({
      q: consulta,
      'resources[type]': tipos,
      'resources[limit]': String(limite),
      'resources[options][unavailable_products]': 'last',
    });
    try {
      const res = await fetch(`/search/suggest.json?${params}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) return {};
      const data = await res.json();
      return data.resources?.results || {};
    } catch (_) {
      return {};
    }
  }

  /**
   * Elige la mejor corrección ortográfica.
   *
   * Las sugerencias de Shopify salen de lo que otra gente buscó, así que
   * vienen con sus propios errores: para "microfno" ofrecía "micrfono de
   * instrumento", que está peor escrito que lo que el usuario tecleó.
   * Se filtra a las que tienen la misma cantidad de palabras y se elige la
   * de menor distancia de edición, exigiendo que sea un cambio pequeño.
   */
  _mejorCorreccion(sugerencias, termino) {
    const objetivo = normalizar(termino);
    const palabras = objetivo.split(' ').length;

    const candidatas = sugerencias
      .map((s) => normalizar(s))
      .filter((s) => s && s !== objetivo && s.split(' ').length === palabras);

    let mejor = null;
    let mejorDist = Infinity;
    for (const c of candidatas) {
      const d = distanciaEdicion(objetivo, c);
      if (d < mejorDist) {
        mejorDist = d;
        mejor = c;
      }
    }
    // Un cambio grande no es una corrección, es otra búsqueda.
    const techo = Math.max(1, Math.floor(objetivo.length * 0.34));
    return mejor && mejorDist <= techo ? mejor : null;
  }

  async getSearchResults(termino) {
    if (!termino || termino.length < 2) {
      this.close();
      return;
    }

    // Las dos variantes en paralelo: la normal aporta cobertura, la del
    // comodín aporta la coincidencia exacta de modelo.
    const [amplia, exacta] = await Promise.all([
      this._pedir(termino, 'product,collection,query', 6),
      this._pedir(conComodin(termino), 'product', 4),
    ]);

    const productos = this._fusionar(exacta.products || [], amplia.products || [], termino);
    const sugerencias = (amplia.queries || []).map((q) => q.text || q.styled_text || '').filter(Boolean);

    this._render({
      productos,
      colecciones: amplia.collections || [],
      marcas: this._marcasDe(productos),
      sugerencias,
      termino,
    });
    this.open();
  }

  /**
   * Coincidencias del comodín primero, después el resto sin repetir.
   * Dentro de cada grupo se sube lo que contiene todos los términos escritos.
   */
  _fusionar(exactos, amplios, termino) {
    // Se deduplica por id y no por url: Shopify le agrega parámetros de
    // tracking distintos (_psq, _psid) a cada consulta, así que el mismo
    // producto llega con dos URLs diferentes desde las dos variantes.
    const vistos = new Set();
    const fuera = [];
    const empujar = (arr) => {
      for (const p of arr) {
        const clave = p.id != null ? String(p.id) : p.url;
        if (vistos.has(clave)) continue;
        vistos.add(clave);
        fuera.push(p);
      }
    };

    const terminos = normalizar(termino).split(' ').filter(Boolean);
    const contieneTodo = (p) => {
      const t = normalizar(p.title + ' ' + (p.sku || '') + ' ' + (p.vendor || ''));
      return terminos.every((x) => t.includes(x));
    };

    const ordenar = (arr) => [...arr].sort((a, b) => contieneTodo(b) - contieneTodo(a));

    // Si la consulta parece un modelo, el comodín es el que sabe. Si es
    // genérica, la búsqueda amplia manda y el comodín solo complementa.
    if (pareceModelo(termino)) {
      empujar(ordenar(exactos));
      empujar(ordenar(amplios));
    } else {
      empujar(ordenar(amplios));
      empujar(ordenar(exactos));
    }
    return fuera.slice(0, 6);
  }

  _marcasDe(productos) {
    const cuenta = new Map();
    for (const p of productos) {
      const v = (p.vendor || '').trim();
      if (!v || v.toLowerCase() === 'sin marca' || v.toLowerCase() === 'urdile') continue;
      cuenta.set(v, (cuenta.get(v) || 0) + 1);
    }
    return [...cuenta.keys()].slice(0, 4);
  }

  _render({ productos, colecciones, marcas, sugerencias, termino }) {
    if (!this._dropdown) {
      this._dropdown = document.createElement('div');
      this._dropdown.className = 'predictive-search__dropdown';
      this._dropdown.setAttribute('role', 'listbox');
      this.appendChild(this._dropdown);
    }

    if (!productos.length && !colecciones.length) {
      this._dropdown.innerHTML = this._sinResultados(termino, sugerencias);
      this._bindCierre();
      return;
    }

    let html = '';

    // Corrección ortográfica: solo si lo sugerido difiere de lo escrito.
    const correccion = this._mejorCorreccion(sugerencias, termino);
    if (correccion) {
      html += `<div class="predictive-search__correction">
        ¿Buscabas <a href="/search?q=${encodeURIComponent(correccion)}&type=product"><strong>${this._esc(correccion)}</strong></a>?
      </div>`;
    }

    if (productos.length) {
      html += `<div class="predictive-search__section">
        <p class="predictive-search__section-title">Productos</p>
        <ul role="none">
          ${productos.map((p, i) => `
            <li role="option" class="predictive-search__item" data-index="${i}">
              <a href="${p.url}" class="predictive-search__link">
                ${p.image
                  ? `<img src="${p.image}" alt="" width="40" height="40" loading="lazy" class="predictive-search__img">`
                  : '<div class="predictive-search__img-placeholder"></div>'}
                <div class="predictive-search__info">
                  <span class="predictive-search__title">${this._resaltar(p.title, termino)}</span>
                  <span class="predictive-search__meta">
                    ${p.vendor ? `<span class="predictive-search__vendor">${this._esc(p.vendor)}</span>` : ''}
                    ${p.price ? `<span class="predictive-search__price">${typeof formatMoney === 'function' ? formatMoney(p.price) : p.price}</span>` : ''}
                  </span>
                </div>
              </a>
            </li>`).join('')}
        </ul>
      </div>`;
    }

    if (marcas.length) {
      html += `<div class="predictive-search__section">
        <p class="predictive-search__section-title">Marcas</p>
        <div class="predictive-search__chips">
          ${marcas.map((m) => `
            <a href="/collections/vendors?q=${encodeURIComponent(m)}" class="predictive-search__chip">${this._esc(m)}</a>
          `).join('')}
        </div>
      </div>`;
    }

    if (colecciones.length) {
      html += `<div class="predictive-search__section">
        <p class="predictive-search__section-title">Categorías</p>
        <div class="predictive-search__chips">
          ${colecciones.slice(0, 4).map((c) => `
            <a href="${c.url}" class="predictive-search__chip">${this._esc(c.title)}</a>
          `).join('')}
        </div>
      </div>`;
    }

    html += `<div class="predictive-search__footer">
      <a href="/search?q=${encodeURIComponent(termino)}&type=product" class="predictive-search__all">
        Ver todos los resultados para “${this._esc(termino)}”
      </a>
    </div>`;

    this._dropdown.innerHTML = html;
    this._bindCierre();
  }

  /** Punto 8: "Mensaje útil cuando no existen resultados". */
  _sinResultados(termino, sugerencias) {
    const correccion = this._mejorCorreccion(sugerencias, termino);
    const wa = `https://wa.me/${WA_URDILE}?text=${encodeURIComponent('Hola, busco: ' + termino)}`;
    return `
      <div class="predictive-search__empty">
        <p class="predictive-search__empty-title">Sin resultados para “${this._esc(termino)}”</p>
        ${correccion
          ? `<p class="predictive-search__empty-hint">¿Buscabas <a href="/search?q=${encodeURIComponent(correccion)}&type=product"><strong>${this._esc(correccion)}</strong></a>?</p>`
          : '<p class="predictive-search__empty-hint">Prueba con la marca, el modelo o el SKU. Ejemplo: <em>RCF 705</em> o <em>VS1050</em>.</p>'}
        <div class="predictive-search__chips">
          <a href="/collections/cat_audio_profesional" class="predictive-search__chip">Audio Profesional</a>
          <a href="/collections/cat_iluminacion_profesional" class="predictive-search__chip">Iluminación</a>
          <a href="/collections/cat_dj_produccion_musical_y_home_studio" class="predictive-search__chip">DJ y Estudio</a>
          <a href="/collections/cat_rigging_y_montaje" class="predictive-search__chip">Rigging</a>
        </div>
        <p class="predictive-search__empty-hint">
          ¿No lo encuentras? Muchos equipos los traemos a pedido.
          <a href="${wa}" target="_blank" rel="noopener"><strong>Consúltanos por WhatsApp</strong></a>.
        </p>
      </div>`;
  }

  _bindCierre() {
    this._dropdown.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', () => this.close());
    });
  }

  open() {
    if (!this._dropdown) return;
    this._isOpen = true;
    this._dropdown.removeAttribute('hidden');
    this._dropdown.classList.add('predictive-search__dropdown--open');
    this._input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this._dropdown) return;
    this._isOpen = false;
    this._dropdown.setAttribute('hidden', '');
    this._dropdown.classList.remove('predictive-search__dropdown--open');
    this._input.setAttribute('aria-expanded', 'false');
    this._currentIndex = -1;
    this._quitarActivo();
  }

  getQuery() {
    return this._input ? this._input.value.trim() : '';
  }

  _onKeydown(event) {
    const items = this._dropdown
      ? Array.from(this._dropdown.querySelectorAll('.predictive-search__item'))
      : [];

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._currentIndex = Math.min(this._currentIndex + 1, items.length - 1);
        this._activar(items, this._currentIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this._currentIndex = Math.max(this._currentIndex - 1, -1);
        this._activar(items, this._currentIndex);
        if (this._currentIndex === -1) this._input.focus();
        break;

      case 'Enter':
        // Con un item resaltado se va a ese producto; sin nada resaltado se
        // deja pasar para que el formulario navegue a la página de resultados.
        if (this._currentIndex >= 0 && items[this._currentIndex]) {
          const link = items[this._currentIndex].querySelector('a');
          if (link) {
            event.preventDefault();
            link.click();
          }
        }
        break;

      case 'Escape':
        this.close();
        this._input.blur();
        break;
    }
  }

  _activar(items, index) {
    this._quitarActivo();
    if (index >= 0 && items[index]) {
      items[index].classList.add('predictive-search__item--active');
      items[index].setAttribute('aria-selected', 'true');
      items[index].scrollIntoView({ block: 'nearest' });
    }
  }

  _quitarActivo() {
    if (!this._dropdown) return;
    this._dropdown.querySelectorAll('.predictive-search__item--active').forEach((el) => {
      el.classList.remove('predictive-search__item--active');
      el.removeAttribute('aria-selected');
    });
  }

  _resaltar(texto, consulta) {
    const seguro = this._esc(texto);
    const terminos = normalizar(consulta).split(' ').filter((t) => t.length >= 2);
    if (!terminos.length) return seguro;
    const patron = terminos.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    return seguro.replace(new RegExp(`(${patron})`, 'gi'), '<mark>$1</mark>');
  }

  _esc(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str == null ? '' : String(str)));
    return div.innerHTML;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
